import { ArrowRight, Phone, Mail } from "lucide-react";
import { WhatsAppIcon } from "./PhoneLinks";
import Chapter from "./Chapter";
import { Reveal } from "./Reveal";
import { Flag } from "./Flagify";
import { PARTNERS } from "../data/partners";
import { useLang } from "../i18n";

// Compact "spotlight" band: every partner with a direct WhatsApp enquiry link.
export const PartnerSpotlight = ({ index = "07", partners = PARTNERS, title, lead }) => {
  const { t } = useLang();
  const s = t.spotlight;
  const p = t.partners;

  return (
    <section className="border-y border-[#DDE4EA] bg-white/70" data-testid="partner-spotlight">
      <div className="mx-auto max-w-[1760px] px-5 py-20 sm:px-8 sm:py-28">
        <Chapter index={index} label={s.label} title={title || s.title} lead={lead || s.lead} />

        <div className={`mt-12 grid gap-6 sm:grid-cols-2 ${partners.length > 2 ? "xl:grid-cols-4" : "lg:max-w-3xl"}`}>
          {partners.map((partner, i) => {
            const waText = encodeURIComponent(p.waPrefill.replace("{name}", partner.first));
            return (
              <Reveal key={partner.id} delay={i * 0.08} className="nv-card flex h-full flex-col p-6" data-testid={`spotlight-card-${partner.id}`}>
                <div className="flex items-center gap-4">
                  <img
                    src={partner.photo}
                    alt={partner.name}
                    loading="lazy"
                    decoding="async"
                    className="h-16 w-16 shrink-0 rounded-full object-cover object-top ring-1 ring-[#DDE4EA]"
                    data-testid={`spotlight-photo-${partner.id}`}
                  />
                  <div className="min-w-0">
                    <span className="flex items-center gap-1.5" data-testid={`spotlight-flags-${partner.id}`}>
                      {(partner.flags || [partner.flag]).map((c) => <Flag key={c} code={c} />)}
                    </span>
                    <p className="mt-1.5 font-display text-base font-extrabold leading-tight tracking-tight text-[#0E1B24]" data-testid={`spotlight-name-${partner.id}`}>{partner.name}</p>
                  </div>
                </div>

                <p className="mt-4 text-[13px] font-semibold leading-snug text-[#0F5C8C]">{partner.role}</p>
                <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#5E7280]">{partner.location}</p>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {partner.focus.slice(0, 3).map((f) => (
                    <span key={f} className="border border-[#DDE4EA] bg-[#F4F6F8] px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#41535F]">{f}</span>
                  ))}
                </div>

                <div className="mt-auto pt-6">
                  <a
                    href={`https://wa.me/${partner.whatsapp}?text=${waText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`spotlight-whatsapp-${partner.id}`}
                    className="nv-cta-glow inline-flex min-h-[46px] w-full items-center justify-center gap-2 bg-[#25D366] px-4 text-[13px] font-bold uppercase tracking-wide text-white"
                  >
                    <WhatsAppIcon size={16} /> {s.whatsapp.replace("{name}", partner.first)} <ArrowRight size={14} />
                  </a>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <a href={`tel:+${partner.whatsapp}`} data-testid={`spotlight-call-${partner.id}`} className="inline-flex min-h-[42px] items-center justify-center gap-2 border border-[#C8D3DB] px-3 text-xs font-semibold text-[#0E1B24] transition-colors hover:border-[#0F5C8C] hover:text-[#0F5C8C]">
                      <Phone size={14} /> {t.hub.callUs}
                    </a>
                    <a href={`mailto:${partner.email}`} data-testid={`spotlight-email-${partner.id}`} className="inline-flex min-h-[42px] items-center justify-center gap-2 border border-[#C8D3DB] px-3 text-xs font-semibold text-[#0E1B24] transition-colors hover:border-[#0F5C8C] hover:text-[#0F5C8C]">
                      <Mail size={14} /> {t.hub.email}
                    </a>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="mt-8 max-w-4xl text-[11px] leading-relaxed text-[#5E7280]" data-testid="spotlight-note">{p.note}</p>
      </div>
    </section>
  );
};

export default PartnerSpotlight;
